/**
 * <pas-floating-cart> — slides in at the bottom of the viewport when the
 * hero product card scrolls out of view on the PAS product template.
 *
 * Also wires the "Read more" toggle inside the hero card description.
 *
 * No framework deps — plain custom element.
 */

class TencFloatingCart extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.product-pas');
    if (!section) return;

    this.section = section;
    // Track the main ATC (buy block) — not the whole card — so the floating
    // bar only appears once the real Add-to-cart button leaves the viewport.
    this.target =
      section.querySelector('[data-pas-buy] .add-to-cart-button') ||
      section.querySelector('[data-pas-buy]') ||
      section.querySelector('[data-pas-card]');
    if (!this.target) return;

    this.removeAttribute('hidden');

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // An off-screen target can be either above or below the viewport.
          // Only show the bar after the shopper has actually scrolled past
          // the real ATC; otherwise it flashes at page load while the ATC is
          // still below the fold on mobile.
          const visible = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          if (visible) {
            this.setAttribute('data-visible', 'true');
          } else {
            this.removeAttribute('data-visible');
          }
        }
      },
      {
        threshold: 0,
        // Trigger slightly before the ATC fully leaves the viewport so the
        // floating bar slides in just as the in-card button goes out of reach.
        rootMargin: '0px 0px -40px 0px',
      }
    );
    this.observer.observe(this.target);

    // Wire description read-more toggle.
    const desc = section.querySelector('[data-pas-description]');
    const toggle = section.querySelector('[data-pas-description-toggle]');
    if (desc && toggle) {
      toggle.addEventListener('click', () => {
        const open = desc.getAttribute('data-open') === 'true';
        desc.setAttribute('data-open', open ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    }

    // Delegate to Horizon's real ATC so variant/add-on/cart-drawer wiring stays
    // single-sourced. If the current variant cannot be added, return the user
    // to the buy block where the unavailable state is explained.
    const cta = this.querySelector('[data-pas-floating-cta]');
    if (cta) {
      cta.addEventListener('click', () => {
        const addToCartButton = section.querySelector('[data-pas-buy] .add-to-cart-button');
        if (addToCartButton && !addToCartButton.disabled) {
          addToCartButton.click();
          return;
        }
        this.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('pas-floating-cart')) {
  customElements.define('pas-floating-cart', TencFloatingCart);
}

/**
 * Wire the "Read more" toggle independently of <pas-floating-cart>.
 * Delegated click handler covers cases where multiple cards exist
 * (e.g. quick-view) and survives DOM re-renders from variant updates.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-pas-description-toggle]');
  if (!toggle) return;
  const desc = toggle.closest('[data-pas-description]');
  if (!desc) return;
  const open = desc.getAttribute('data-open') === 'true';
  desc.setAttribute('data-open', open ? 'false' : 'true');
  toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
});
